import {
  Component,
  computed,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { combineLatest, distinctUntilChanged, filter, take } from 'rxjs';

import { DialogCloseReason, DialogService } from '@sui/dialog';
import { AdvancedSearchService } from '../../services/advanced-search.service';
import { UserFacadeService } from '@services/user-facade.service';

import * as feedActions from '../../actions/feed.actions';
import { AppFeatures } from '../../app.features';
import { AppState } from 'src/app/reducers';
import { Feed } from '../../models/core.models';
import {
  NomObjectClass,
  SortDirection,
  feedSortFieldApplies,
} from '../../constants';
import { FeedPostViewMode } from 'src/app/api/services/feed-api.service';
import {
  PostAlgorithm,
  PostAlgorithmService,
} from '../../services/post-algorithm.service';
import { PropertyViewConfig } from '../../services/feed-settings.service';
import {
  ConfirmDialogComponent,
  ConfirmDialogOutput,
} from '../popups/confirm-dialog/confirm-dialog.component';
import {
  selectCurrentObject,
  selectFeed,
  selectFeedPostsViewMode,
  selectFeedSettings,
  selectFeedTimeShown,
  selectSelectedPostsCount,
  selectUser,
} from '../../reducers';
import { TimeShown } from '../../reducers/feed.reducer';
import {
  injectFeedViewConfigQuery,
  injectToggleFeedViewConfigOption,
} from 'src/app/api/queries/view-preset.queries';
import { FeedPostsService } from '../../services/feed-posts.service';
import {
  injectClearFeedSelectionMutation,
  injectRemoveSelectedPostsFromFeedMutation,
  injectSetFeedPostViewModeMutation,
} from 'src/app/api/queries/feed.queries';
import { injectTogglePostsSelectionMutation } from 'src/app/api/queries/post.queries';
import { isShadowPostId } from 'src/app/shared/utils/shadow-post.utils';

/** A menu row rendered inside one of the toolbar's dropdown panels. */
interface FeedMenuItem {
  text?: string;
  icon?: IconProp;
  value?: string;
  key?: string;
  isSeparator?: boolean;
  isTitle?: boolean;
  isToggle?: boolean;
  isRadio?: boolean;
  isSelected?: boolean;
  radioGroup?: string;
  action?: () => void;
  /** Trailing affordance (e.g. "schedule" on a feed-wide algorithm). */
  secondaryAction?: { icon: IconProp; tooltip?: string; action: () => void };
}

/**
 * A consecutive run of view rows. `radioGroup` set ⇒ the run is one mutually
 * exclusive group and renders inside a single `sui-radio-group` (a radio group
 * needs one wrapping element, and `role=radiogroup` must not enclose toggles).
 */
interface FeedMenuGroup {
  radioGroup?: string;
  /** Currently selected `value` within a radio run. */
  value?: string;
  items: FeedMenuItem[];
}

/**
 * Feed-results toolbar, projected into `post-list` via `[postListToolbar]`.
 * Feed-only: subscribes to feed store state to build its dropdowns and dispatches
 * directly (replaces the retired `FeedActionsService` config + bus).
 */
@Component({
  selector: 'app-feed-results-toolbar',
  templateUrl: './feed-results-toolbar.component.html',
  styleUrl: './feed-results-toolbar.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FeedResultsToolbarComponent {
  private store = inject(Store<AppState>);
  private translate = inject(TranslateService);
  private algorithmService = inject(PostAlgorithmService);
  private userFacade = inject(UserFacadeService);
  private dialogService = inject(DialogService);
  private advancedSearchService = inject(AdvancedSearchService);
  private feedPosts = inject(FeedPostsService);

  // Permission gates (mirror the old FeedActionsService config gating).
  readonly canSelectContent = this.userFacade.hasPermissionSync(
    AppFeatures.FEED_SEARCH_RESULTS_SELECT_CONTENT,
  );
  readonly canMassOps = this.userFacade.hasPermissionSync(
    AppFeatures.ADVANCED_MASS_OPERATIONS_ROOT,
  );
  readonly canView = this.userFacade.hasPermissionSync(
    AppFeatures.FEED_SEARCH_RESULTS_SETTINGS_DISPLAY_SETTINGS,
  );
  readonly canSort = this.userFacade.hasPermissionSync(
    AppFeatures.FEED_SEARCH_RESULTS_SETTINGS_SORT_SETTINGS,
  );
  private readonly canRemoveFromFeed = this.userFacade.hasPermissionSync(
    AppFeatures.ADVANCED_MASS_OPERATIONS_REMOVE_FROM_FEED,
  );
  private readonly canShowSelectedOnly = this.userFacade.hasPermissionSync(
    AppFeatures.ADVANCED_MASS_OPERATIONS_SELECTED_ITEMS_IN_CURRENT_FEED_TAB,
  );

  /**
   * The feed's `pv-feed-results` view configuration. Observed here and in
   * `feed.component`; the shared query key means the two cost one request.
   */
  private readonly viewConfigQuery = injectFeedViewConfigQuery(
    this.store.selectSignal(selectFeed),
    this.store.selectSignal(selectUser),
  );
  private readonly toggleViewConfigOption = injectToggleFeedViewConfigOption();

  private readonly removeSelectedMutation =
    injectRemoveSelectedPostsFromFeedMutation();

  private readonly clearSelectionMutation = injectClearFeedSelectionMutation();
  private readonly selectionMutation = injectTogglePostsSelectionMutation();
  private readonly setPostViewModeMutation =
    injectSetFeedPostViewModeMutation();
  private readonly user = this.store.selectSignal(selectUser);

  private feed: Feed;
  private viewPostsMode: string;
  private timeShown: TimeShown;

  selectAllChecked = false;
  showSelectedOnly = false;
  feedSearchQuery = '';

  hasSelectedPosts = false;
  selectedCount = 0;
  selectedItems: FeedMenuItem[] = [];
  actionItems: FeedMenuItem[] = [];
  viewItems: FeedMenuItem[] = [];
  /** `viewItems` split into runs so each radio run can sit in one sui-radio-group. */
  viewGroups: FeedMenuGroup[] = [];
  sortFieldItems: FeedMenuItem[] = [];
  readonly sortOrderItems: FeedMenuItem[] = [
    {
      text: this.translate.instant('feed-actions.button.descending'),
      icon: ['fas', 'sort-amount-down'],
      value: 'desc',
      isRadio: true,
      radioGroup: 'sort-order',
    },
    {
      text: this.translate.instant('feed-actions.button.ascending'),
      icon: ['fas', 'sort-amount-up'],
      value: 'asc',
      isRadio: true,
      radioGroup: 'sort-order',
    },
  ];
  sortField: string;
  sortDirection: string;

  /** Full sort catalogue; filtered per feed `object_type_id` into `sortFieldItems`. */
  private readonly allSortItems: FeedMenuItem[] = this.buildSortCatalogue();

  get selectAllTooltip(): string {
    return this.translate.instant('feed-actions.button.select-all');
  }
  get selectedLabel(): string {
    return this.translate.instant('feed-actions.button.selected');
  }
  get actionsLabel(): string {
    return this.translate.instant('feed-actions.button.actions');
  }
  get viewLabel(): string {
    return this.translate.instant('feed-actions.button.view');
  }
  get sortLabel(): string {
    return this.translate.instant('feed-actions.button.sort');
  }
  get sortDirIcon(): IconProp {
    return this.sortDirection === 'asc'
      ? ['fas', 'arrow-up-a-z']
      : ['fas', 'arrow-down-z-a'];
  }
  get sortFieldIcon(): IconProp {
    return this.sortFieldItems.find((i) => i.value === this.sortField)?.icon;
  }

  constructor() {
    this.store
      .select(selectFeed)
      .pipe(takeUntilDestroyed())
      .subscribe((feed) => {
        if (!feed) {
          return;
        }
        this.feed = feed;
        this.sortFieldItems = this.allSortItems.filter((item) =>
          this.sortApplies(item, feed.object_type_id),
        );
        this.selectedCount = feed.count_selected_posts || 0;
        this.hasSelectedPosts = this.selectedCount > 0;
        // Clearing the selection unchecks select-all.
        if (!this.hasSelectedPosts) {
          this.selectAllChecked = false;
        }
      });

    combineLatest([
      this.store.select(selectUser),
      this.store.select(selectCurrentObject).pipe(
        filter((obj) => !!obj && obj.object_class_id === NomObjectClass.FEED),
        distinctUntilChanged((a, b) => a.id === b.id),
      ),
    ])
      .pipe(takeUntilDestroyed())
      .subscribe(([user, currentObject]) => {
        const algorithms = this.algorithmService
          .getAlgorithmsPerUser(user, currentObject)
          .sort((a, b) => a.group?.localeCompare(b.group));
        this.actionItems = this.buildActionItems(algorithms);
        this.selectedItems = this.buildSelectedItems(algorithms);
      });

    this.store
      .select(selectFeedSettings)
      .pipe(takeUntilDestroyed())
      .subscribe((feedSettings) => {
        this.sortDirection = feedSettings.sort_direction;
        this.sortField = feedSettings.order_field;
      });

    // view_posts_mode and post_time_mode radios track the store, not
    // config.selected - see `buildViewItems`.
    combineLatest([
      toObservable(computed(() => this.viewConfigQuery.data())),
      this.store.select(selectFeedPostsViewMode),
      this.store.select(selectFeedTimeShown),
    ])
      .pipe(takeUntilDestroyed())
      .subscribe(([viewConfig, viewPostsMode, timeShown]) => {
        this.viewPostsMode = viewPostsMode;
        this.timeShown = timeShown;

        // A saved preset is the load-time source of truth for the card date, so
        // push it into the store. Without a preset the feed-derived `timeShown`
        // from `loadFeedSuccess` stands and the radio below aligns to it, so the
        // dropdown and the cards never disagree on load.
        if (viewConfig?.fromPreset) {
          this.applyPresetTimeShown(viewConfig.config, timeShown);
        }

        this.viewItems = this.buildViewItems(viewConfig?.config ?? []);
        this.viewGroups = this.groupViewItems(this.viewItems);
      });

    this.advancedSearchService.searchQuery$
      .pipe(takeUntilDestroyed())
      .subscribe((query) => (this.feedSearchQuery = query));
  }

  onSelectAllChange(selected: boolean): void {
    this.selectAllChecked = selected;

    // * The cache write is the visible half: it flips `selected` on every real,
    // * selectable post, and the dispatch moves the feed-wide counter by the net
    // * change it answers. The POST only mirrors the selection server-side and is
    // * deliberately not awaited. Parents and shadow copies are skipped by both.
    const posts = this.feedPosts
      .list()
      .filter((post) => !post.is_parent && !isShadowPostId(post.id));

    const delta = this.feedPosts.setAllSelected(selected);

    this.selectionMutation.mutate({
      userId: this.user()?.id,
      feedId: this.feed?.id,
      posts,
      selected,
    });

    this.store.dispatch(
      feedActions.selectAllPosts({ feedId: this.feed?.id, selected, delta }),
    );
    setTimeout(() => window.dispatchEvent(new Event('resize')), 50);
  }

  onRefresh(): void {
    this.feedPosts.invalidate();
  }

  onSortFieldChange(value: string): void {
    this.sortField = value;
    this.store.dispatch(feedActions.sortChanged({ sortField: value }));
  }

  onSortOrderChange(value: string): void {
    this.sortDirection = value;
    this.store.dispatch(
      feedActions.sortDirectionChanged({
        sortDirection: value as SortDirection,
      }),
    );
  }

  private buildSelectedItems(algorithms: PostAlgorithm[]): FeedMenuItem[] {
    const items: FeedMenuItem[] = [];

    if (this.canRemoveFromFeed) {
      items.push(
        {
          text: this.translate.instant(
            'feed-actions.selected.remove-from-feed',
          ),
          icon: ['far', 'times-circle'],
          action: () => this.removeSelected(),
        },
        { isSeparator: true },
      );
    }
    if (this.canShowSelectedOnly) {
      items.push(
        {
          text: this.translate.instant(
            'feed-actions.selected.show-selected-only',
          ),
          icon: ['fas', 'toggle-on'],
          isToggle: true,
          action: () => this.toggleShowSelectedOnly(),
        },
        { isSeparator: true },
      );
    }

    items.push(
      {
        isTitle: true,
        text: this.translate.instant(
          'feed-actions.heading.apply-action-to-selected-items',
        ),
      },
      { isSeparator: true },
      ...this.algorithmItems(algorithms, (algorithm) => ({
        action: () => this.runAlgorithmSelected(algorithm.id),
      })),
      { isSeparator: true },
      {
        text: this.translate.instant('feed-actions.button.clear-selection'),
        icon: ['fas', 'times'],
        action: () => this.clearSelection(),
      },
    );

    return items;
  }

  private buildActionItems(algorithms: PostAlgorithm[]): FeedMenuItem[] {
    return [
      {
        isTitle: true,
        text: this.translate.instant(
          'feed-actions.heading.apply-action-to-all-items',
        ),
      },
      { isSeparator: true },
      ...this.algorithmItems(algorithms, (algorithm) => ({
        action: () => this.runAlgorithmFeed(algorithm.id, false),
        secondaryAction: algorithm.canSchedule
          ? {
              icon: ['far', 'clock'],
              tooltip: this.translate.instant(
                'feed-actions.button.schedule-tooltip',
              ),
              action: () => this.runAlgorithmFeed(algorithm.id, true),
            }
          : undefined,
      })),
    ];
  }

  /** Flatten grouped algorithms into rows, inserting a separator on group change. */
  private algorithmItems(
    algorithms: PostAlgorithm[],
    handlersFor: (
      algorithm: PostAlgorithm,
    ) => Pick<FeedMenuItem, 'action' | 'secondaryAction'>,
  ): FeedMenuItem[] {
    const rows: FeedMenuItem[] = [];
    let currentGroup = algorithms[0]?.group;
    algorithms.forEach((algorithm) => {
      if (currentGroup !== algorithm.group) {
        currentGroup = algorithm.group;
        rows.push({ isSeparator: true });
      }
      rows.push({
        text: algorithm.name,
        icon: algorithm.icon,
        value: String(algorithm.id),
        ...handlersFor(algorithm),
      });
    });
    return rows;
  }

  /**
   * Adopt the preset's `post_time_mode` radio as the feed's `timeShown`. Only
   * dispatches on an actual change: this runs on every emission of the combined
   * stream, and the dispatch feeds back into it through `selectFeedTimeShown`.
   */
  private applyPresetTimeShown(
    config: PropertyViewConfig[],
    timeShown: TimeShown,
  ): void {
    const presetTimeShown = config.find(
      (option) => option.radioGroup === 'post_time_mode' && option.selected,
    )?.path as TimeShown | undefined;

    if (!presetTimeShown || presetTimeShown === timeShown) return;

    this.store.dispatch(
      feedActions.setTimeShownOption({ option: presetTimeShown }),
    );
  }

  private buildViewItems(config: PropertyViewConfig[]): FeedMenuItem[] {
    const createItems = (configItem: PropertyViewConfig): FeedMenuItem[] => {
      if (configItem.isTitle) {
        return [
          { isSeparator: true },
          { text: configItem.label, isTitle: true },
        ];
      }
      return [
        {
          text: configItem.label,
          icon: configItem.icon,
          // Radio rows need a value to bind a sui-radio-group against.
          value: String(configItem.path ?? configItem.label),
          isToggle: !configItem.isRadio,
          isRadio: configItem.isRadio,
          isSelected:
            configItem.radioGroup === 'view_posts_mode'
              ? configItem.path === this.viewPostsMode
              : configItem.radioGroup === 'post_time_mode'
                ? configItem.path === this.timeShown
                : configItem.selected,
          radioGroup: configItem.radioGroup,
          action: () => this.onViewConfigChange(configItem),
        },
      ];
    };

    const tools = config.filter((c) => c.visibility?.includes('tools'));
    const properties = config.filter((c) => !c.visibility?.includes('tools'));

    const items: FeedMenuItem[] = [
      ...tools.flatMap(createItems),
      {
        isTitle: true,
        text: this.translate.instant('feed-actions.heading.metadata'),
      },
      ...properties.flatMap(createItems),
    ];

    // Drop a leading separator (a tools group emits one before its title).
    return items.filter((item, index) => !(index === 0 && item.isSeparator));
  }

  /** Collapses consecutive rows sharing a `radioGroup` into one group. */
  private groupViewItems(items: FeedMenuItem[]): FeedMenuGroup[] {
    const groups: FeedMenuGroup[] = [];

    for (const item of items) {
      const previous = groups[groups.length - 1];
      const runs =
        !!item.radioGroup && previous?.radioGroup === item.radioGroup;

      if (runs) {
        previous.items.push(item);
        if (item.isSelected) {
          previous.value = item.value;
        }
        continue;
      }

      groups.push({
        radioGroup: item.radioGroup,
        value: item.isSelected ? item.value : undefined,
        items: [item],
      });
    }

    return groups;
  }

  /** A radio run owns the whole group's selection; delegate to the picked row. */
  onViewRadioChange(group: FeedMenuGroup, value: unknown): void {
    group.items.find((item) => item.value === String(value))?.action?.();
  }

  private onViewConfigChange(configItem: PropertyViewConfig): void {
    if (configItem.isRadio && configItem.radioGroup === 'view_posts_mode') {
      const viewMode = String(configItem.path) as FeedPostViewMode;

      this.setPostViewModeMutation
        .mutateAsync({ feedId: this.feed?.id, viewMode })
        .then(() => {
          // * the reducer patches `feed.view_posts_mode` off this broadcast.
          this.store.dispatch(
            feedActions.setFeedPostViewModeSuccess({ viewMode }),
          );
        })
        .catch(() => undefined);
    } else {
      this.toggleViewConfigOption(this.feed?.id, configItem.label);

      // The post cards render from `timeShown`, not from the view config, so
      // without this the toggle only moves the dropdown's radio and the dates on
      // the cards never change (the setTimeShownOption dispatch was lost in
      // WAP-831 / WAP-1497).
      if (configItem.radioGroup === 'post_time_mode') {
        this.store.dispatch(
          feedActions.setTimeShownOption({
            option: configItem.path as TimeShown,
          }),
        );
      }
    }
  }

  private toggleShowSelectedOnly(): void {
    this.showSelectedOnly = !this.showSelectedOnly;
    this.store.dispatch(
      feedActions.showSelectedPostsOnly({
        feedId: this.feed?.id,
        showSelectedOnly: this.showSelectedOnly,
      }),
    );
  }

  private clearSelection(): void {
    // * Same split as above: the cache write deselects every rendered post, the
    // * dispatch zeroes the counter, the POST is not awaited.
    this.feedPosts.clearSelection();
    this.store.dispatch(
      feedActions.clearFeedSelectedItems({ feedId: this.feed?.id }),
    );
    this.clearSelectionMutation.mutate({
      userId: this.user()?.id,
      feedId: this.feed?.id,
    });
  }

  private runAlgorithmSelected(algorithmId: number): void {
    this.store
      .select(selectSelectedPostsCount)
      .pipe(take(1))
      .subscribe((itemCount) =>
        this.algorithmService.runPostAlgorithmSelected(
          this.feed?.id,
          algorithmId,
          itemCount,
          {},
          false,
        ),
      );
  }

  private runAlgorithmFeed(algorithmId: number, schedule: boolean): void {
    this.algorithmService.runPostAlgorithmFeed(
      this.feed?.id,
      algorithmId,
      this.feedPosts.totalItemsCount(),
      {},
      schedule,
    );
  }

  private removeSelected(): void {
    const applyToChild = this.userFacade.hasPermissionSync(
      AppFeatures.ADVANCED_MASS_OPERATIONS_REMOVE_FROM_FEED_APPLY_TO_CHILD,
    );
    const options = applyToChild
      ? [
          {
            key: 'applyToChildObjects',
            label: this.translate.instant('option.apply-to-child-objects'),
            value: false,
          },
        ]
      : [];

    const dialogRef = this.dialogService.openSimpleDialog<
      ConfirmDialogComponent,
      ConfirmDialogOutput
    >(ConfirmDialogComponent, {
      data: {
        message: this.translate.instant('remove-selected.posts.modal-message'),
        options,
      },
      closeOnClick: false,
    });

    dialogRef.closed.subscribe((event) => {
      if (event.reason !== DialogCloseReason.Confirm) {
        return;
      }
      const applyToChildObjects = event.data?.options?.find(
        (opt) => opt.key === 'applyToChildObjects',
      )?.value;
      // * the hook's onSuccess owns the content refresh and the
      // * `count_selected_posts` reset broadcast
      this.removeSelectedMutation
        .mutateAsync({
          feedId: this.feed?.id,
          applyToChildObjects: !!applyToChildObjects,
        })
        .catch(() => undefined);
    });
  }

  private sortApplies(item: FeedMenuItem, objectTypeId: number): boolean {
    return feedSortFieldApplies(item.value, objectTypeId);
  }

  private buildSortCatalogue(): FeedMenuItem[] {
    const t = (key: string) => this.translate.instant(key);
    const fields: FeedMenuItem[] = [
      {
        value: 'post_time_published',
        icon: ['fas', 'upload'],
        text: t('feed-actions.button.published-time'),
      },
      {
        value: 'post_time_created',
        icon: ['fas', 'circle-plus'],
        text: t('feed-actions.button.created-time'),
      },
      {
        value: 'post_reach',
        icon: ['fab', 'chromecast'],
        text: t('feed-actions.button.potential-reach'),
      },
      {
        value: 'post_reach_v2',
        icon: ['fab', 'chromecast'],
        text: t('feed-actions.button.estimated-reach'),
      },
      {
        value: 'post_source_visits_per_day',
        icon: ['fab', 'chromecast'],
        text: t('feed-actions.button.source-daily-visits'),
      },
      {
        value: 'post_rank',
        icon: ['fas', 'chart-line'],
        text: t('feed-actions.button.content-rank'),
      },
      {
        value: 'post_title',
        icon: ['fas', 'font'],
        text: t('feed-actions.button.title'),
      },
      {
        value: 'source_rank_global',
        icon: ['fas', 'trophy'],
        text: t('feed-actions.button.global-rank'),
      },
      {
        value: 'source_rank_country',
        icon: ['fas', 'sort-numeric-up'],
        text: t('feed-actions.button.country-rank'),
      },
      {
        value: 'post_social_followers',
        icon: ['fas', 'users'],
        text: t('feed-actions.button.followers'),
      },
      {
        value: 'post_social_reactions_count',
        icon: ['fas', 'heart-pulse'],
        text: t('feed-actions.button.social-reactions'),
      },
      {
        value: 'post_social_interactions_count',
        icon: ['fas', 'hand-pointer'],
        text: t('feed-actions.button.social-interactions'),
      },
      {
        value: 'post_social_engagements_count',
        icon: ['fas', 'hand-sparkles'],
        text: t('feed-actions.button.social-engagements'),
      },
      {
        value: 'post_social_shares_count',
        icon: ['fas', 'retweet'],
        text: t('feed-actions.button.social-shares'),
      },
      {
        value: 'post_social_comments_count',
        icon: ['fas', 'comments'],
        text: t('feed-actions.button.social-comments'),
      },
      {
        value: 'post_social_likes_count',
        icon: ['fas', 'heart'],
        text: t('feed-actions.button.social-likes'),
      },
      {
        value: 'post_social_dislikes_count',
        icon: ['fas', 'thumbs-down'],
        text: t('feed-actions.button.social-dislikes'),
      },
    ];
    return fields.map((item) => ({
      ...item,
      isRadio: true,
      radioGroup: 'sort',
    }));
  }
}
